"use client";

import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AuthService from "@/lib/auth.ts";
import useAuth from "@/hooks/useAuth.ts";
import useDispatchAction from "@/hooks/useDispatchAction.ts";
import { setAuth } from "@/state/reducers/auth.slice.ts";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { validateNPI, formatPhoneDash, handlePhoneInputChange, handlePhoneKeyDown } from "@/lib/utils";

// Base schema for the form
const baseSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email"),
  role: z.string().optional(),
  auth_uuid: z.string().optional(),
  location_ids: z.any().optional(), // Can be string or array from DB
  npi: z.string().optional().refine(validateNPI, {
    message: "Invalid NPI: Must be 10 digits starting with 1 or 2, and have a valid checksum",
  }),
  phone_number: z.string().optional().refine(
    (v) => {
      if (!v) return true;
      const digits = v.replace(/\D/g, "");
      return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
    },
    "Phone number must be exactly 10 digits",
  ),
  primary_doctor_associated: z.string().optional(),
  secondary_doctor_associated: z.string().optional(),
  primary_doctor_name: z.string().optional(),
  secondary_doctor_name: z.string().optional(),
  associated_branch: z.union([z.string(), z.number()]).optional(),
});



type ProfileFormValues = z.infer<typeof baseSchema>;

export default function ProfileForm({
  defaultValues,
}: {
  defaultValues: Partial<ProfileFormValues>;
}) {
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useDispatchAction();
  // Tracks the role value after the form is reset from DB data — used to
  // detect a real role change vs the form simply loading its initial value.
  const loadedRoleRef = useRef<string>(defaultValues.role || "");
  const [roles, setRoles] = useState<string[]>([]);
  const [rolesMetadata, setRolesMetadata] = useState<{ role: string; is_clinical: boolean; is_standalone_cp: boolean; is_allowed_to_add_notes: boolean }[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  const ADMIN_ONLY_ROLES = ['Admin', 'SuperAdmin'];

  const isClinical = (roleName: string) => {
    return rolesMetadata.find(r => r.role === roleName)?.is_clinical || false;
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(true);
  const [branches, setBranches] = useState<any[]>([]);
  const [doctorsList, setDoctorsList] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Fetch branches and doctors for selection
  useEffect(() => {
    const fetchSupportData = async () => {
      try {
        setDataLoading(true);
        const hospitalRes = await AuthService.getHospitalsAndBranches();

        // Flatten state-grouped response into individual clinic locations
        const flatBranches = (Array.isArray(hospitalRes) ? hospitalRes : []).flatMap((state: any) => {
          const locs = state.locations || state.clinic_locations || [];
          return locs.map((loc: any) => ({
            id: loc.id,
            name: `${loc.hospital_name || state.state_name || ""} — ${loc.name}`,
          }));
        });
        setBranches(flatBranches);

        // Filter providers by the user's associated_branch (location id)
        const userBranch = defaultValues.associated_branch;
        const providerParams = userBranch ? { location_id: userBranch } : undefined;
        const doctorRes = await AuthService.getProviders(providerParams);
        setDoctorsList(doctorRes);
      } catch (err) {
        console.error("Error fetching support data:", err);
      } finally {
        setDataLoading(false);
      }
    };
    fetchSupportData();
  }, [defaultValues]);

  // Derive isOwnProfile if we have the target user ID and current auth user ID
  useEffect(() => {
    if (auth?.user?.id && defaultValues.id) {
      setIsOwnProfile(auth.user.id === defaultValues.id || auth.user.username === defaultValues.auth_uuid);
    }
  }, [auth, defaultValues]);

  // Fallback roles if the API is unavailable — keep in sync with mhai_non_prod.user_role enum
  // Fallback roles if the API is unavailable — keep in sync with mhai_non_prod.user_role enum
  const FALLBACK_ROLES: string[] = ['Admin', 'Doctor', 'Nurse', 'Staff', 'Technician', 'Clinical_reviewer', 'Nutritionist'];

  // Initialize form with base schema
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(baseSchema),
    defaultValues: {
      id: defaultValues.id || "",
      name: defaultValues.name || "",
      email: defaultValues.email || "",
      role: defaultValues.role || "",
      auth_uuid: defaultValues.auth_uuid || "",
      npi: defaultValues.npi || "",
      phone_number: (defaultValues as any).phone_number || "",
      primary_doctor_associated: defaultValues.primary_doctor_associated || "",
      secondary_doctor_associated: defaultValues.secondary_doctor_associated || "",
      primary_doctor_name: defaultValues.primary_doctor_name || "",
      secondary_doctor_name: defaultValues.secondary_doctor_name || "",
      associated_branch: defaultValues.associated_branch || "",
      location_ids: defaultValues.location_ids || "",
    },
  });

  // Derive isClinical and isStandalone live from the currently watched role + metadata — must be after useForm
  const watchedRole = form.watch("role");
  const derivedIsClinical = (() => {
    if (!watchedRole || rolesMetadata.length === 0) return null;
    const match = rolesMetadata.find(
      (r) => r.role.toLowerCase() === watchedRole.toLowerCase()
    );
    return match ? match.is_clinical : null;
  })();

  // is_standalone_cp = TRUE means Doctor / CareCoordinator — supervision anchors.
  // These roles have NPI but must NOT have primary/secondary CP associations.
  const derivedIsStandalone = (() => {
    if (!watchedRole || rolesMetadata.length === 0) return null;
    const match = rolesMetadata.find(
      (r) => r.role.toLowerCase() === watchedRole.toLowerCase()
    );
    return match ? match.is_standalone_cp : null;
  })();

  // Debug: Log auth data to see what's available
  useEffect(() => {
    console.log('🔍 Auth Debug Info:', {
      auth: auth,
      user: auth?.user,
      userRole: auth?.user?.role,
      defaultValues: defaultValues
    });
  }, [auth, defaultValues]);

  // Fetch roles + metadata from the API — single source of truth for names and is_clinical
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        const metadata = await AuthService.getRolesMetadata();
        if (metadata.length > 0) {
          setRolesMetadata(metadata);
          const sorted = metadata
            .map((r: any) => r.role as string)
            .filter(Boolean)
            .filter((r: string, i: number, arr: string[]) => arr.indexOf(r) === i)
            .sort();
          setRoles(sorted);
        } else {
          // Metadata empty — fall back to name-only list
          const allRoles = await AuthService.getRoles();
          const sorted = (allRoles.length ? allRoles : FALLBACK_ROLES)
            .filter((r, i, arr) => arr.indexOf(r) === i)
            .sort();
          setRoles(sorted);
        }
      } catch (err) {
        console.error('❌ Error fetching roles:', err);
        setRoles([...FALLBACK_ROLES].sort());
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Fetch current user role to check if SuperAdmin
  useEffect(() => {
    const checkAdmin = async () => {
      if (auth?.user) {
        const profile = await AuthService.getProfile(auth.user.id);
        const role = profile?.role?.toLowerCase();
        setIsSuperAdmin(role === 'admin' || profile?.role_id === 1);
      }
    };
    checkAdmin();
  }, [auth]);

  // Derive canEditRole based on user request logic:
  // "staff themselves cannot assign them roles... 
  // or change role if they are clinical only can change to other non-clinical if they are non-clinical"
  const canEditRole = (() => {
    if (isSuperAdmin) return true; // SuperAdmin can change anything

    // If it's your own profile, you cannot change your role
    if (isOwnProfile) return false;

    const currentUserRole = auth?.user?.role?.toLowerCase();
    if (currentUserRole === 'branchadmin') {
      // BranchAdmin cannot change if the target is clinical
      if (derivedIsClinical === true) return false;
      return true;
    }

    return false;
  })();

  // Fetch current user data from database if not in defaultValues
  useEffect(() => {
    const fetchCurrentUserData = async () => {
      if (!auth?.user?.id) return;

      try {
        console.log('🔄 Fetching current user data from AWS for Auth ID:', auth.user.id);
        const data = await AuthService.getProfile(auth.user.id);

        if (data) {
          console.log('✅ User data found:', data);
          updateFormWithUserData(data);
        } else {
          console.warn('⚠️ No profile data returned from AWS');
        }
      } catch (err) {
        console.error('❌ Unexpected error fetching user data:', err);
      } finally {
        setUserDataLoaded(true);
      }
    };

    const updateFormWithUserData = (userData: any) => {
      const formData = {
        id: userData.id || "",
        name: userData.full_name || userData.name || defaultValues.name || "",
        email: userData.email || defaultValues.email || "",
        role: userData.role || defaultValues.role || "",
        auth_uuid: userData.auth_uuid || "",
        npi: userData.npi || "",
        phone_number: formatPhoneDash((userData as any).phone_number || ""),
        primary_doctor_associated: userData.primary_doctor_associated || "",
        secondary_doctor_associated: userData.secondary_doctor_associated || "",
        associated_branch: userData.associated_branch || "",
        location_ids: userData.location_ids || "",
        // Enriched name fields from backend _enrich_user — used as fallback
        // display while doctorsList is still loading asynchronously.
        primary_doctor_name: userData.primary_doctor_name || "",
        secondary_doctor_name: userData.secondary_doctor_name || "",
      } as any;

      console.log('📝 Updating form with user data:', {
        userData,
        formData,
        tableId: userData.id,
        authUuid: userData.auth_uuid
      });
      form.reset(formData);
      // Record the role that was loaded from DB — this is the baseline
      // for detecting a real role change on submit.
      loadedRoleRef.current = formData.role || "";

      // Refresh doctorsList using the actual associated_branch from the DB
      // so that saved primary/secondary CP UUIDs find their match in the list.
      if (userData.associated_branch) {
        AuthService.getProviders({ location_id: userData.associated_branch })
          .then(setDoctorsList)
          .catch(console.error);
      } else {
        // No branch — fetch all providers so UUIDs can still match
        AuthService.getProviders()
          .then(setDoctorsList)
          .catch(console.error);
      }
    };

    // ONLY fetch if we don't have enough data in defaultValues (e.g., on the My Profile page)
    // and only if we haven't loaded data yet.
    // If we are on StaffDetails, defaultValues will have auth_uuid already.
    if (auth?.user?.id && !userDataLoaded) {
      fetchCurrentUserData();
    }
  }, [auth?.user?.id, defaultValues, form, userDataLoaded]);

  // Helper function to update auth state
  const updateAuthState = (userData: any, updatedFields: any) => {
    // Update auth state
    const updatedUser = {
      ...auth.user,
      ...userData,
      role: userData.role || null,
    };

    dispatch(setAuth({
      token: auth.token,
      user: updatedUser,
      expiresIn: auth.expiresIn
    }));

    // Show appropriate success message based on what was updated
    if (updatedFields.role !== undefined) {
      toast.success("Profile updated successfully! Role has been updated.");
    } else {
      toast.success("Profile updated successfully!");
    }

    // Do NOT force a re-fetch/reset here — it clears isDirty and makes the
    // button colour flicker. The auth slice update above is sufficient.
  };

  // FIXED SUBMIT FUNCTION
  const onSubmit = async (values: ProfileFormValues) => {
    if (!auth?.user?.id) {
      toast.error("User not authenticated");
      return;
    }

    try {
      setIsSubmitting(true);
      const targetId = values.id || defaultValues.id || auth.user.id;
      console.log('🚀 Submitting form for Target ID:', targetId);

      const updatePayload: any = {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        associated_branch: values.associated_branch ? Number(values.associated_branch) : null,
        location_ids: values.location_ids,
      };

      // Only send role when it has actually changed — sending the existing role
      // triggers the backend's role-elevation check and returns a 403 even
      // though no role change was intended.
      const originalRole = defaultValues.role ?? null;
      const submittedRole = values.role && values.role.trim() !== "" ? values.role.trim() : null;
      if (submittedRole !== originalRole) {
        updatePayload.role = submittedRole;
      }

      // Only include optional fields if they have a real value
      if (values.npi && String(values.npi).trim() !== "") updatePayload.npi = values.npi;
      if ((values as any).phone_number && String((values as any).phone_number).trim() !== "") {
        const cleanDigits = String((values as any).phone_number).replace(/\D/g, "").replace(/^1/, "").slice(-10);
        updatePayload.phone_number = cleanDigits ? `+1${cleanDigits}` : null;
      }
      if (values.primary_doctor_associated && String(values.primary_doctor_associated).trim() !== "") updatePayload.primary_doctor_associated = values.primary_doctor_associated;
      if (values.secondary_doctor_associated && String(values.secondary_doctor_associated).trim() !== "") updatePayload.secondary_doctor_associated = values.secondary_doctor_associated;

      console.log('📤 Update payload:', updatePayload);

      // Use updateStaff to ensure all RDS fields are covered
      const result = await AuthService.updateStaff(targetId, updatePayload);

      if (result.error) {
        console.error('❌ Update failed:', result.error);
        toast.error(`Update failed: ${result.error.message || 'Unknown error'}`);
        return;
      }

      console.log('✅ Update successful:', result);

      // Update local auth state ONLY if editing own profile
      if (isOwnProfile) {
        const roleChanged = values.role && values.role.trim() !== "" && values.role !== loadedRoleRef.current;
        updateAuthState(result, roleChanged ? { role: values.role } : {});
        navigate("/dashboard");
      } else {
        toast.success("Staff profile updated successfully!");
        navigate("/admin/staff"); // Go back to list after editing someone else
      }

    } catch (err: any) {
      console.error('❌ Unexpected error:', err);
      toast.error(err.message || "Something went wrong during update");
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <Card className="w-full max-w-xl mx-auto mt-4 sm:mt-6 md:mt-10 p-4 sm:p-6">
      <CardHeader className="px-0 sm:px-0 pt-0 sm:pt-0 pb-4">
        <h2 className="text-xl sm:text-2xl font-semibold">Update Profile</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Update your personal information and role settings
        </p>
      </CardHeader>

      <CardContent className="px-0 sm:px-0 py-2 sm:py-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
            {/* Name Field */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm sm:text-base">Full Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter your full name"
                      disabled={isSubmitting}
                      className="text-sm sm:text-base h-10 sm:h-11"
                    />
                  </FormControl>
                  <FormMessage className="text-xs sm:text-sm" />
                </FormItem>
              )}
            />

            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm sm:text-base">Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      {...field}
                      placeholder="your.email@example.com"
                      disabled={isSubmitting}
                      className="text-sm sm:text-base h-10 sm:h-11"
                    />
                  </FormControl>
                  <FormMessage className="text-xs sm:text-sm" />
                </FormItem>
              )}
            />

            {/* Password Field - Link to Change Password Page */}
            <div className="flex flex-col space-y-2">
              <FormLabel className="text-sm sm:text-base">Password</FormLabel>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto justify-start"
                onClick={() => navigate('/update-password')}
              >
                Change Password
              </Button>
            </div>

            {/* Role Field with Enhanced Display */}
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-col space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <FormLabel className="text-sm sm:text-base">Role</FormLabel>
                      {field.value && (
                        <Badge variant="secondary" className="text-2xs sm:text-xs">
                          Current: {formatRoleName(field.value)}
                        </Badge>
                      )}
                      {derivedIsClinical !== null && (
                        <Badge
                          className={`text-2xs sm:text-xs font-medium ${derivedIsClinical
                              ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                              : "bg-slate-100 text-slate-600 border border-slate-200"
                            }`}
                        >
                          {derivedIsClinical ? "🩺 Clinical" : "🏢 Non-Clinical"}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <FormControl>
                    <Select
                      value={field.value || undefined}
                      onValueChange={(value) => {
                        field.onChange(value);
                        console.log('Role changed to:', value);
                      }}
                      disabled={rolesLoading || isSubmitting || !canEditRole}
                    >
                      <SelectTrigger className="mt-1 h-10 sm:h-11 text-sm sm:text-base">
                        <SelectValue placeholder={rolesLoading ? "Loading..." : "Select your role"}>
                          {rolesLoading
                            ? "Loading..."
                            : field.value
                              ? formatRoleName(field.value)
                              : "Select your role"
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => {
                          const isAdminOnly = ADMIN_ONLY_ROLES.includes(role);
                          // BranchAdmin can only see/select non-clinical roles
                          const isBranchAdminRestricted = auth?.user?.role?.toLowerCase() === 'branchadmin' && isClinical(role);
                          const isDisabled = isAdminOnly || isBranchAdminRestricted;

                          return (
                            <SelectItem
                              key={role}
                              value={role}
                              disabled={isDisabled}
                              className={isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                            >
                              <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                <span className="text-sm sm:text-base">{formatRoleName(role)}</span>
                                {field.value === role && (
                                  <Badge variant="outline" className="text-2xs sm:text-xs">Current</Badge>
                                )}
                                {isAdminOnly && (
                                  <Badge variant="outline" className="text-2xs sm:text-xs">Contact Admin</Badge>
                                )}
                                {isBranchAdminRestricted && (
                                  <Badge variant="outline" className="text-2xs sm:text-xs">Clinical - Restricted</Badge>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* NPI Field — show ONLY for roles that are allowed to add notes
                (Doctor, CareCoordinator, Nurse Practitioner, Physician Assistant).
                Completely hidden for non-note-writing clinical roles (Nurse, Technician,
                Clinical_reviewer) and all non-clinical roles — NPI is not applicable. */}
            {(() => {
              const roleMetadata = rolesMetadata.find(
                (r) => r.role.toLowerCase() === (watchedRole || "").toLowerCase()
              );
              const isAllowedToAddNotes = roleMetadata
                ? roleMetadata.is_allowed_to_add_notes
                : false;
              // Only show if role explicitly allows notes, OR if a legacy NPI
              // value already exists in the DB (so existing data is not hidden).
              const showNpi = isAllowedToAddNotes || !!defaultValues.npi;
              if (!showNpi) return null;
              return (
                <FormField
                  control={form.control}
                  name="npi"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>NPI Number</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter 10-digit NPI"
                          maxLength={10}
                          onInput={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.value = target.value.replace(/\D/g, "");
                            field.onChange(target.value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              );
            })()}

            {/* Phone Number — optional for all roles */}
            <FormField
              control={form.control}
              name={"phone_number" as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number <span className="text-gray-400 font-normal text-xs">(optional)</span></FormLabel>
                  <FormControl>
                    <div className="relative flex items-center">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm pointer-events-none select-none">+1</span>
                      <Input
                        placeholder="###-###-####"
                        maxLength={12}
                        className="pl-9"
                        value={formatPhoneDash(field.value || "")}
                        onChange={(e) => {
                          handlePhoneInputChange(e, (formatted) => {
                            field.onChange(formatted);
                          }, formatPhoneDash);
                        }}
                        onKeyDown={handlePhoneKeyDown}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Branch Assignment - All roles */}
            <FormField
              control={form.control}
              name="associated_branch"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Main Associated Branch</FormLabel>
                  <Select
                    onValueChange={(val) => {
                      field.onChange(val);
                      // Re-fetch providers for the new branch
                      AuthService.getProviders({ location_id: val }).then(setDoctorsList).catch(console.error);
                    }}
                    value={field.value ? String(field.value) : undefined}
                    disabled={dataLoading || isSubmitting}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Branch" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {branches.map(b => (
                        <SelectItem key={b.id || b.uuid} value={String(b.id || b.uuid)}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Care Provider Associations —
                Only for supervised clinical roles: is_clinical=TRUE AND is_standalone_cp=FALSE
                (Nurse, Nurse Practitioner, Physician Assistant, Clinical_reviewer, Technician).
                Standalone CP anchors (Doctor, CareCoordinator) must NOT have these fields. */}
            {derivedIsClinical === true && derivedIsStandalone === false && (
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="primary_doctor_associated"
                  render={({ field }) => {
                    // Resolve display name: prefer list match, fall back to enriched name from API
                    const savedName = defaultValues.primary_doctor_name;
                    const matchInList = doctorsList.find(d => d.id === field.value);
                    const displayPlaceholder = field.value && !matchInList && savedName
                      ? `${savedName} (loading...)`
                      : "No Primary CP Assigned";
                    return (
                      <FormItem>
                        <FormLabel>Primary Care Provider</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger className="bg-gray-50/50">
                              <SelectValue placeholder={displayPlaceholder} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {/* Fallback: inject saved CP as an option if doctorsList hasn't loaded yet
                                or doesn't contain the saved UUID. Prevents the Radix Select from
                                showing a blank/placeholder when the value is set but no matching
                                SelectItem exists in the DOM. Removed once doctorsList resolves. */}
                            {field.value && !doctorsList.find(d => d.id === field.value) && (
                              <SelectItem key={field.value} value={field.value}>
                                {defaultValues.primary_doctor_name || field.value}
                              </SelectItem>
                            )}
                            {doctorsList.map(doc => (
                              <SelectItem key={doc.id} value={doc.id}>{doc.name} ({doc.role})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="secondary_doctor_associated"
                  render={({ field }) => {
                    const savedName = defaultValues.secondary_doctor_name;
                    const matchInList = doctorsList.find(d => d.id === field.value);
                    const displayPlaceholder = field.value && !matchInList && savedName
                      ? `${savedName} (loading...)`
                      : "No Secondary CP Assigned";
                    return (
                      <FormItem>
                        <FormLabel>Secondary Care Provider</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger className="bg-gray-50/50">
                              <SelectValue placeholder={displayPlaceholder} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {/* Fallback: inject saved secondary CP as an option if doctorsList
                                hasn't loaded yet or doesn't contain the saved UUID. Same timing
                                race fix as primary CP above. */}
                            {field.value && !doctorsList.find(d => d.id === field.value) && (
                              <SelectItem key={field.value} value={field.value}>
                                {defaultValues.secondary_doctor_name || field.value}
                              </SelectItem>
                            )}
                            {doctorsList.map(doc => (
                              <SelectItem key={doc.id} value={doc.id}>{doc.name} ({doc.role})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            )}

            {/* FIXED SUBMIT BUTTON */}
            <Button
              type="submit"
              disabled={isSubmitting || rolesLoading || !form.formState.isDirty}
              className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium"
              variant={form.formState.isDirty ? "default" : "outline"}
              size="lg"
            >
              {isSubmitting ? "Updating..." : "Update Profile"}
            </Button>
          </form>
        </Form>
      </CardContent>

      <CardFooter className="px-0 sm:px-0 pt-4 sm:pt-6">
        <div className="w-full space-y-3">
          <p className="text-xs sm:text-sm text-muted-foreground">
            Your profile information is secure and encrypted.
          </p>

          {/* Enhanced User Info Display */}
          {auth?.user && (
            <div className="text-xs sm:text-sm bg-muted/50 p-3 rounded-lg space-y-2">
              <div className="truncate">
                <span className="font-medium">Current User:</span> {auth.user.name || auth.user.email}
              </div>
              {form.watch("role") ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium">Current Role:</span>
                  <Badge variant="default" className="text-2xs sm:text-xs">
                    {formatRoleName(form.watch("role") || "No Role")}
                  </Badge>
                  {derivedIsClinical !== null && (
                    <Badge
                      className={`text-2xs sm:text-xs font-semibold px-2 py-0.5 rounded-full border ${derivedIsClinical
                          ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                          : "bg-slate-100 text-slate-500 border-slate-300"
                        }`}
                    >
                      {derivedIsClinical ? "🩺 Clinical Staff" : "🏢 Non-Clinical"}
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="text-orange-600 text-sm">
                  <span className="font-medium">⚠️ No role assigned</span> - Please select a role above
                </div>
              )}
            </div>
          )}

          {/* DEBUG INFO */}
          {/* <div className="text-xs bg-gray-100 p-2 rounded">
            <div><strong>Roles loaded:</strong> {roles.length} ({rolesLoading ? 'Loading...' : 'Ready'})</div>
            <div><strong>Available roles:</strong> {roles.join(', ')}</div>
            <div><strong>Current form role:</strong> {form.watch("role") || 'None'}</div>
          </div> */}
        </div>
      </CardFooter>
    </Card>
  );
}

export const formatRoleName = (role: string) => {
  return role.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
};